import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Button,
    Typography,
    Snackbar,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import Header from '../../Header';
import { local_url, local_signage } from '../../Const';
import { LocalCreditCustomerForm } from './LocalCreditCustomerForm';

// Field casing isn't confirmed for this endpoint's response, so check the common variants.
const getBranchId = (branch) => branch?.BranchId || branch?.branchId || branch?.BranchID || branch?.branchID || '';
const getBranchName = (branch) => branch?.BranchName || branch?.branchName || getBranchId(branch);

export const LocalCreditCustomer = () => {
    const token = localStorage.getItem('AuthorizationToken');
    const userId = localStorage.getItem('userId');

    // Branch / Area filter dropdowns
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [areas, setAreas] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(false);

    const [filterBranchId, setFilterBranchId] = useState('ALL');
    const [filterAreaId, setFilterAreaId] = useState('ALL');

    // List
    const [rows, setRows] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Add/Edit popup
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState('add');
    const [editingRecord, setEditingRecord] = useState(null);

    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const handleCloseNotification = () => {
        setNotification((prev) => ({ ...prev, open: false }));
    };

    const handleAuthError = () => {
        showNotification('Session expired. Please log in again.', 'error');
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/Login';
    };

    // Load the Branch dropdown once, on mount
    useEffect(() => {
        const fetchBranches = async () => {
            setLoadingBranches(true);
            try {
                const response = await fetch(
                    `${local_signage}/api/v1/fetchbranchbyareabizsegment?AreaId=ALL&BizSegmentCode=LAZIZ`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (response.status === 401) { handleAuthError(); return; }
                if (!response.ok) throw new Error('Failed to fetch branches');
                const data = await response.json();
                const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
                setBranches(list);
            } catch (error) {
                console.error('Error fetching branches:', error);
                showNotification('Could not load the Branch list.', 'error');
            } finally {
                setLoadingBranches(false);
            }
        };
        fetchBranches();
    }, []);

    // Load the Area dropdown once, on mount
    useEffect(() => {
        const fetchAreas = async () => {
            if (!token || !userId) return;
            setLoadingAreas(true);
            try {
                const response = await fetch(`${local_url}/api/SFArea/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('Failed to fetch areas');
                const data = await response.json();
                setAreas(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching areas:', error);
                showNotification('Could not load the Area list.', 'error');
            } finally {
                setLoadingAreas(false);
            }
        };
        fetchAreas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the Local Credit Customer list, filtered by Branch/Area.
    // NOTE: this endpoint doesn't exist yet — the backend team is adding it.
    // Expected response: an array (or { data: [...] }) of objects with
    // CustId, ForBranchId, CreditLimit, CreditDays, PricingSchemeType.
    const fetchList = useCallback(async () => {
        setLoadingList(true);
        try {
            const response = await fetch(
                `${local_signage}/api/v1/fetchlocalcreditcustomer?BranchId=${filterBranchId}&AreaId=${filterAreaId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.status === 401) { handleAuthError(); return; }
            if (!response.ok) throw new Error('Failed to fetch local credit customer list');
            const data = await response.json();
            const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
            setRows(list);
        } catch (error) {
            console.error('Error fetching local credit customer list:', error);
            showNotification('Could not load the Local Credit Customer list.', 'error');
            setRows([]);
        } finally {
            setLoadingList(false);
        }
    }, [filterBranchId, filterAreaId]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const handleOpenAdd = () => {
        setFormMode('add');
        setEditingRecord(null);
        setFormOpen(true);
    };

    const handleOpenEdit = (row) => {
        setFormMode('edit');
        setEditingRecord(row);
        setFormOpen(true);
    };

    const handleFormClose = () => {
        setFormOpen(false);
        setEditingRecord(null);
    };

    const handleFormSaved = () => {
        setFormOpen(false);
        setEditingRecord(null);
        fetchList();
    };

    const filteredRows = rows.filter((row) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        const custId = String(row.CustId || row.custId || '').toLowerCase();
        const customerName = String(row.CustomerName || row.customerName || '').toLowerCase();
        return custId.includes(term) || customerName.includes(term);
    });

    const resolveBranchName = (branchId) => {
        const match = branches.find((b) => getBranchId(b) === branchId);
        return match ? getBranchName(match) : branchId;
    };

    const escapeCsvValue = (value) => {
        const stringValue = value === null || value === undefined ? '' : String(value);
        return /[",\n]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
    };

    const handleExportCsv = () => {
        const headers = ['Customer Code', 'Customer Name', 'Area Name', 'Branch', 'Credit Limit', 'Credit Days', 'Pricing Scheme Type'];
        const csvRows = filteredRows.map((row) => {
            const custId = row.CustId || row.custId;
            const branchId = row.ForBranchId || row.forBranchId;
            const customerName = row.CustomerName || row.customerName || '';
            const areaName = row.SFAreaName || row.sfAreaName || row.AreaName || row.areaName || '';
            return [
                custId,
                customerName,
                areaName,
                row.BranchName || row.branchName || resolveBranchName(branchId),
                row.CreditLimit ?? row.creditLimit,
                row.CreditDays ?? row.creditDays,
                row.PricingSchemeType || row.pricingSchemeType
            ]
                .map(escapeCsvValue)
                .join(',');
        });
        const csvContent = [headers.join(','), ...csvRows].join('\r\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'LocalCreditCustomer.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <Header />
            <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <CardHeader
                        avatar={<PersonIcon sx={{ color: '#004d00' }} />}
                        title={
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#004d00' }}>
                                Local Credit Customer
                            </Typography>
                        }
                        action={
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, mr: 1 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenAdd}
                                    sx={{ bgcolor: '#004d00', '&:hover': { bgcolor: '#003300' } }}
                                >
                                    New
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={handleExportCsv}
                                    disabled={filteredRows.length === 0}
                                    sx={{ bgcolor: '#004d00', '&:hover': { bgcolor: '#003300' } }}
                                >
                                    Export CSV
                                </Button>
                            </Box>
                        }
                    />
                    <CardContent>
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Area</InputLabel>
                                    <Select
                                        label="Area"
                                        value={filterAreaId}
                                        onChange={(e) => setFilterAreaId(e.target.value)}
                                        disabled={loadingAreas}
                                    >
                                        <MenuItem value="ALL">ALL</MenuItem>
                                        {areas.map((area) => (
                                            <MenuItem key={area.sfAreaId} value={area.sfAreaId}>
                                                {area.sfAreaName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Branch</InputLabel>
                                    <Select
                                        label="Branch"
                                        value={filterBranchId}
                                        onChange={(e) => setFilterBranchId(e.target.value)}
                                        disabled={loadingBranches}
                                    >
                                        <MenuItem value="ALL">ALL</MenuItem>
                                        {branches.map((branch) => (
                                            <MenuItem key={getBranchId(branch)} value={getBranchId(branch)}>
                                                {getBranchName(branch)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} sx={{ ml: 'auto' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Search by Customer ID or Name"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#f5f5f5' } }}>
                                        <TableCell>Customer Code</TableCell>
                                        <TableCell>Customer Name</TableCell>
                                        <TableCell>Area Name</TableCell>
                                        <TableCell>Branch</TableCell>
                                        <TableCell>Credit Limit</TableCell>
                                        <TableCell>Credit Days</TableCell>
                                        <TableCell>Pricing Scheme Type</TableCell>
                                        <TableCell align="center">Edit</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loadingList ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                                <CircularProgress size={22} />
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                No records found.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredRows.map((row, index) => {
                                            const custId = row.CustId || row.custId;
                                            const branchId = row.ForBranchId || row.forBranchId;
                                            const customerName = row.CustomerName || row.customerName || '';
                                            const areaName = row.SFAreaName || row.sfAreaName || row.AreaName || row.areaName || '';
                                            return (
                                                <TableRow
                                                    key={`${custId}-${index}`}
                                                    hover
                                                    sx={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#e8f5e9' }}
                                                >
                                                    <TableCell>{custId}</TableCell>
                                                    <TableCell>{customerName}</TableCell>
                                                    <TableCell>{areaName}</TableCell>
                                                    <TableCell>{row.BranchName || row.branchName || resolveBranchName(branchId)}</TableCell>
                                                    <TableCell>{row.CreditLimit ?? row.creditLimit}</TableCell>
                                                    <TableCell>{row.CreditDays ?? row.creditDays}</TableCell>
                                                    <TableCell>{row.PricingSchemeType || row.pricingSchemeType}</TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                                                                <EditIcon fontSize="small" sx={{ color: '#004d00' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>

            <LocalCreditCustomerForm
                open={formOpen}
                mode={formMode}
                editingRecord={editingRecord}
                branches={branches}
                loadingBranches={loadingBranches}
                onClose={handleFormClose}
                onSaved={handleFormSaved}
                showNotification={showNotification}
                handleAuthError={handleAuthError}
            />

            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default LocalCreditCustomer;
